import React, { useCallback, useEffect, useMemo, useState } from "react";
import ActivityStatsCards from "./models/activity_models/ActivityStatsCards";
import ActivityToolbar from "./models/activity_models/ActivityToolbar";
import ActivityTimeline from "./models/activity_models/ActivityTimeline";
import { caApi } from "../../../backend_urls/caApi";

const formatAction = (action) => {
    const labels = {
        created: "created a task",
        updated: "updated a task",
        assigned: "assigned a task",
        unassigned: "unassigned a task",
        started: "started a task",
        paused: "paused a task",
        resumed: "resumed a task",
        submitted: "submitted a task for review",
        approved: "approved a task",
        rejected: "rejected a task",
        completed: "completed a task",
        cancelled: "cancelled a task",
        commented: "commented on a task",
        priority_changed: "changed task priority",
        due_date_changed: "changed a task due date",
        reassigned: "reassigned a task",
    };

    return labels[action] || String(action || "performed an action")
        .replaceAll("_", " ");
};

const getResponseData = (response) => {
    if (Array.isArray(response)) { return response; }

    if (Array.isArray(response?.data)) { return response.data; }

    if (Array.isArray(response?.data?.results)) {
        return response.data.results;
    }

    if (Array.isArray(response?.results)) { return response.results; }

    return [];
};

const getTaskId = (activity) => {
    if (typeof activity.task === "object" && activity.task !== null) {
        return activity.task.id;
    }

    return activity.task;
};

const getEmployeeId = (activity) => {
    if (typeof activity.employee === "object" && activity.employee !== null) {
        return activity.employee.id;
    }

    return activity.employee;
};

const normalizeActivity = (activity, task) => {
    const employeeName =
        activity.employee_name ||
        activity.employee?.user?.user_full_name ||
        "System";

    const taskTitle =
        activity.task_title ||
        activity.task?.title ||
        task?.title ||
        "Task";

    const businessName =
        activity.business_name ||
        activity.task?.business_name ||
        task?.business_name ||
        task?.business?.business_name ||
        "—";

    const departmentName =
        activity.department_name ||
        activity.task?.department_name ||
        task?.department_name ||
        task?.department?.name ||
        "";

    const officeName =
        activity.office_name ||
        activity.task?.office_name ||
        task?.office_name ||
        task?.office?.name ||
        "";

    const status =
        activity.task_status ||
        activity.task?.status ||
        task?.status ||
        "";

    return {
        ...activity,
        id: activity.id,
        task_id: getTaskId(activity),
        employee_id: getEmployeeId(activity),
        employee: employeeName,
        employee_name: employeeName,
        task_title: taskTitle,
        action: activity.action,
        action_label: formatAction(activity.action),
        business: businessName,
        business_name: businessName,
        department: departmentName,
        department_name: departmentName,
        office: officeName,
        office_name: officeName,
        status,
        created_at: activity.created_at,
        remarks: activity.remarks || "",
    };
};

const CATeamsActivity = () => {
    const [activities, setActivities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState("");

    const [filters, setFilters] = useState({
        search: "",
        employee: "",
        department: "",
        status: "",
        action: "",
    });

    const loadActivities = useCallback(async (isRefresh = false) => {
        try {
            if (isRefresh) { setRefreshing(true);
            } else { setLoading(true); }

            setError("");

            const taskResponse = await caApi.getTasks();
            const tasks = getResponseData(taskResponse);

            if (!tasks.length) {
                setActivities([]);
                return;
            }

            const activityResponses = await Promise.all(
                tasks.map(async (task) => {
                    try {
                        const response = await caApi.getTaskActivities(task.id);

                        return {
                            task,
                            activities: getResponseData(response),
                        };
                    } catch (activityError) {
                        console.error(
                            `Failed to load activities for task ${task.id}`,
                            activityError
                        );

                        return { task, activities: [] };
                    }
                })
            );

            const combined = activityResponses
                .flatMap(({ task, activities: taskActivities }) =>
                    taskActivities.map((activity) =>
                        normalizeActivity(activity, task)
                    )
                )
                .sort(
                    (a, b) =>
                        new Date(b.created_at || 0) -
                        new Date(a.created_at || 0)
                );

            setActivities(combined);
        } catch (err) {
            console.error("Failed to load team activities:", err);

            setError(
                err?.response?.data?.detail ||
                "Unable to load team activities."
            );
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, []);

    useEffect(() => { loadActivities(); }, [loadActivities]);

    const updateFilter = useCallback((key, value) => {
        setFilters((previous) => ({
            ...previous,
            [key]: value,
        }));
    }, []);

    const resetFilters = useCallback(() => {
        setFilters({
            search: "",
            employee: "",
            department: "",
            status: "",
            action: "",
        });
    }, []);

    const filteredActivities = useMemo(() => {
        const search = filters.search.trim().toLowerCase();

        return activities.filter((activity) => {
            const matchesSearch =
                !search ||
                    [
                        activity.employee,
                        activity.task_title,
                        activity.business,
                        activity.action_label,
                        activity.remarks,
                    ]
                        .filter(Boolean)
                        .some((value) =>
                            String(value).toLowerCase().includes(search)
                        );

            const matchesEmployee =
                !filters.employee ||
                String(activity.employee_id) === String(filters.employee);

            const matchesDepartment =
                !filters.department ||
                String(activity.department_name) ===
                    String(filters.department);

            const matchesStatus =
                !filters.status ||
                String(activity.status) === String(filters.status);

            const matchesAction =
                !filters.action ||
                String(activity.action) === String(filters.action);

            return (
                matchesSearch &&
                matchesEmployee &&
                matchesDepartment &&
                matchesStatus &&
                matchesAction
            );
        });
    }, [activities, filters]);

    const employees = useMemo(() => {
        const map = new Map();

        activities.forEach((activity) => {
            if (
                activity.employee_id &&
                activity.employee &&
                !map.has(String(activity.employee_id))
            ) {
                map.set(String(activity.employee_id), {
                    id: activity.employee_id,
                    name: activity.employee,
                });
            }
        });

        return Array.from(map.values()).sort((a, b) =>
            a.name.localeCompare(b.name)
        );
    }, [activities]);

    const departments = useMemo(() => {
        return Array.from(
            new Set(
                activities
                    .map((activity) => activity.department_name)
                    .filter(Boolean)
            )
        ).sort();
    }, [activities]);

    const stats = useMemo(() => {
        const today = new Date();

        const startOfToday = new Date(today);
        startOfToday.setHours(0, 0, 0, 0);

        const endOfToday = new Date(today);
        endOfToday.setHours(23, 59, 59, 999);

        const todayActivities = activities.filter((activity) => {
            const date = new Date(activity.created_at);

            return (
                date >= startOfToday &&
                date <= endOfToday
            );
        });

        const tasksCompleted = todayActivities.filter(
            (activity) => activity.action === "completed"
        ).length;

        const assignments = todayActivities.filter(
            (activity) =>
                activity.action === "assigned" ||
                activity.action === "reassigned"
        ).length;

        const documentsUploaded = todayActivities.filter(
            (activity) =>
                activity.action === "document_uploaded" ||
                activity.action === "document_upload"
        ).length;

        return {
            todayActivities: todayActivities.length,
            tasksCompleted,
            documentsUploaded,
            assignments,
        };
    }, [activities]);

    return (
        <div>
            <ActivityStatsCards stats={stats} />

            <ActivityToolbar
                filters={filters}
                employees={employees}
                departments={departments}
                onSearchChange={(value) =>
                    updateFilter("search", value)
                }
                onEmployeeChange={(value) =>
                    updateFilter("employee", value)
                }
                onDepartmentChange={(value) =>
                    updateFilter("department", value)
                }
                onStatusChange={(value) =>
                    updateFilter("status", value)
                }
                onActionChange={(value) =>
                    updateFilter("action", value)
                }
                onReset={resetFilters}
                activityCount={filteredActivities.length}
                totalCount={activities.length}
                loading={loading}
                refreshing={refreshing}
                onRefresh={() => loadActivities(true)}
            />

            {error && (
                <div
                    style={{
                        marginBottom: "16px",
                        padding: "12px 16px",
                        borderRadius: "10px",
                        background: "#fef2f2",
                        color: "#b91c1c",
                        border: "1px solid #fecaca",
                    }}
                >
                    {error}
                </div>
            )}

            <ActivityTimeline
                activities={filteredActivities}
                loading={loading}
            />
        </div>
    );
};

export default CATeamsActivity;
